import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Environment } from '../environment/environment';
import { Response } from '../model/response';
import {
  CertificateEmissionRules,
  CertificateImage,
  CertificateTemplate,
  isImageElement,
  sanitizeCertificateTemplate,
  sanitizeEmissionRules,
  serializeCertificateTemplate,
} from '../model/certificate-template';

export interface EventCertificateData {
  template: CertificateTemplate | null;
  images: CertificateImage[];
  /** Regras de emissão DORMENTES: sobreviveram à exclusão do modelo (content
   *  só com `emission`) e revivem quando um novo modelo for criado do zero. */
  dormantEmission: CertificateEmissionRules | null;
}

/**
 * Template de certificado de apresentação (por evento) + imagens do chair.
 *
 * O backend guarda o JSON verbatim (camelCase round-trip do DRF) e as imagens
 * como bytes servidos por GET público (mesmo precedente das fotos de perfil) —
 * as URLs são compostas AQUI e injetadas no template (`url` é volátil; o
 * `serializeCertificateTemplate` a remove antes de persistir/exportar).
 */
@Injectable({ providedIn: 'root' })
export class CertificatesService {

  constructor(private http: HttpClient) {}

  imageUrl(eventId: number, imageId: number): string {
    return `${Environment.urls.API}/core/event/${eventId}/certificate/image/${imageId}/`;
  }

  /** Ids dos eventos que TÊM modelo salvo (p/ o diálogo "copiar de outro evento"). */
  getEventsWithTemplate(): Observable<number[]> {
    return this.http.get<Response>(`${Environment.urls.API}/core/event/certificate/templates/`)
      .pipe(map(v => (v && v.data) || []));
  }

  /** Template + catálogo de imagens do evento; JSON malformado vira null (fallback). */
  getEventCertificate(eventId: number): Observable<EventCertificateData> {
    return this.http.get<Response>(`${Environment.urls.API}/core/event/${eventId}/certificate/template/`)
      .pipe(map(v => {
        const data = (v && v.data) || {};
        const images: CertificateImage[] = (data.images || []).map(i => ({
          id: i.id,
          name: i.name || '',
          url: this.imageUrl(eventId, i.id),
        }));
        const template = sanitizeCertificateTemplate(data.content);
        if (template) { this.attachImageUrls(template, eventId); }
        // Sem modelo, o content pode guardar SÓ as regras dormentes.
        const dormantEmission = !template && data.content
          ? sanitizeEmissionRules(data.content.emission) : null;
        return { template, images, dormantEmission };
      }));
  }

  /**
   * EXCLUI o modelo preservando as regras de emissão DORMENTES no content
   * (`{ emission }`, sem `elements` → tratado como "sem modelo" em toda parte;
   * o índice do backend também o ignora). Sem regras → content null.
   */
  deleteTemplateKeepRules(eventId: number, emission: CertificateEmissionRules | null): Observable<void> {
    const content = emission ? { emission: { allowedStatuses: [...emission.allowedStatuses] } } : null;
    return this.http.put<Response>(`${Environment.urls.API}/core/event/${eventId}/certificate/template/`, { content })
      .pipe(map(() => undefined));
  }

  saveTemplate(eventId: number, template: CertificateTemplate | null): Observable<void> {
    const content = template ? serializeCertificateTemplate(template) : null;
    return this.http.put<Response>(`${Environment.urls.API}/core/event/${eventId}/certificate/template/`, { content })
      .pipe(map(() => undefined));
  }

  uploadImage(eventId: number, file: File): Observable<CertificateImage> {
    const form = new FormData();
    form.append('image', file, file.name);
    form.append('name', file.name);
    return this.http.post<Response>(`${Environment.urls.API}/core/event/${eventId}/certificate/image/`, form)
      .pipe(map(v => ({
        id: v.data.id,
        name: v.data.name || '',
        url: this.imageUrl(eventId, v.data.id),
      })));
  }

  deleteImage(eventId: number, imageId: number): Observable<void> {
    return this.http.delete<Response>(`${Environment.urls.API}/core/event/${eventId}/certificate/image/${imageId}/`)
      .pipe(map(() => undefined));
  }

  /** Resolve `url` de fundo e elementos de imagem a partir dos imageIds. */
  attachImageUrls(template: CertificateTemplate, eventId: number): void {
    if (template.background) {
      template.background.url = this.imageUrl(eventId, template.background.imageId);
    }
    for (const el of template.elements) {
      if (isImageElement(el)) { el.url = this.imageUrl(eventId, el.imageId); }
    }
  }
}
